/**
 * ============================================================
 * Shopify Variant-Image Dedupe (Hybrid: Filename-Key + dHash + Farbe)
 * ============================================================
 * Reine Browser-APIs (Canvas, Image) – keine externen Libraries.
 *
 * Pipeline:
 *  1. Product-Key-Gruppierung (Dateiname-basiert, billig, schnell)
 *  2. Innerhalb jeder Gruppe: dHash-Vergleich (visuelle STRUKTUR)
 *  3. Farbsignatur-Vergleich (visuelle FARBE -- dHash allein erkennt
 *     keine Farbvarianten desselben Musters, siehe colorGridDistance)
 *  4. Aspect-Ratio als schneller Vorab-Filter
 *
 * Ein Bild gilt nur als Duplikat, wenn Struktur UND Farbe übereinstimmen.
 */
(function (global) {
    'use strict';

    // ------------------------------------------------------------
    // 1. Product Key Extraktion
    // ------------------------------------------------------------
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const NUMERIC_RE = /^\d+$/;

    function extractProductKey(url) {
        if (!url) return '';

        let filename = url.split('?')[0].split('/').pop() || '';
        filename = filename.replace(/\.(jpg|jpeg|png|webp|gif|avif)$/i, '');

        const parts = filename.split('_');

        // Von hinten nach vorne instabile Segmente abschneiden
        // (UUIDs, reine Zahlen-Segmente wie Produkt-/Bild-IDs)
        while (parts.length > 1) {
            const last = parts[parts.length - 1];
            if (UUID_RE.test(last) || NUMERIC_RE.test(last)) {
                parts.pop();
            } else {
                break;
            }
        }

        return parts.join('_').toLowerCase();
    }

    // ------------------------------------------------------------
    // 2. Bild laden – mit Timeout-Schutz
    // ------------------------------------------------------------
    // Ein hängender Image-Request (kaputte CDN-URL, Netzwerk-Hänger)
    // darf nie den gesamten Promise.all-Batch blockieren.
    function loadImage(url, timeoutMs = 8000) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            let settled = false;

            const timer = setTimeout(() => {
                if (settled) return;
                settled = true;
                reject(new Error('Image load timeout: ' + url));
            }, timeoutMs);

            img.crossOrigin = 'anonymous'; // Shopify CDN setzt CORS-Header
            img.onload = () => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve(img);
            };
            img.onerror = () => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                reject(new Error('Image load failed: ' + url));
            };
            img.src = url;
        });
    }

    // ------------------------------------------------------------
    // 3. dHash (Difference Hash) – horizontal + vertikal
    // ------------------------------------------------------------
    // dHash vergleicht Helligkeits-GRADIENTEN zwischen Nachbarpixeln
    // statt Absolutwerten (wie beim alten aHash). Dadurch deutlich
    // robuster gegenüber Helligkeits-/Kontrastverschiebungen durch
    // Re-Encoding, Kompression oder leicht abweichende CDN-Transforms.
    //
    // WICHTIG: ctx.drawImage(img, 0, 0, w, w) zum direkten Downscale auf
    // die Hash-Grid-Größe wurde bewusst NICHT verwendet. Canvas skaliert
    // dabei bilinear zwischen wenigen Nachbarpixeln an der Zielposition,
    // unabhängig vom tatsächlichen Skalierungsverhältnis. Bei extremen
    // Downscale-Faktoren (gemessen z.B. 2587px -> 10px, also ~258:1) führt
    // das bei hochfrequenten Mustern (z.B. Tierprint mit vielen kleinen,
    // scharfen Flecken) zu Aliasing: Es wird nur eine kleine, je nach
    // Quellauflösung unterschiedliche Pixel-Stichprobe "getroffen" statt
    // wirklich gemittelt. Exakt dasselbe Motiv in zwei Auflösungen (1080px
    // vs. 2587px) erzeugte dadurch Hashes mit Hamming-Distanz 23 statt der
    // erwarteten ~1 -- vollständig falsch-negative Duplikaterkennung.
    //
    // Fix: boxAverageGrayscale() liest JEDEN Quellpixel und mittelt ihn in
    // den passenden Ziel-Bin (echtes Flächen-Mittelungsverfahren / Box
    // Filter). Verifiziert an den Referenzbildern: Hamming-Distanz 0.
    // Tile-basiertes Lesen hält den Speicherbedarf auch bei sehr großen
    // Bildern (mehrere Megapixel) konstant niedrig.

    let sharedCanvas = null;
    let sharedCtx = null;
    function getSharedCanvasCtx(w, h) {
        if (!sharedCanvas) {
            sharedCanvas = document.createElement('canvas');
            sharedCtx = sharedCanvas.getContext('2d', { willReadFrequently: true });
        }
        if (sharedCanvas.width !== w) sharedCanvas.width = w;
        if (sharedCanvas.height !== h) sharedCanvas.height = h;
        return sharedCtx;
    }

    // ~4 MB ImageData-Puffer pro Tile (1.000.000 Pixel * 4 Byte) -- sicher
    // genug auch für mobile Browser, groß genug um die Tile-Anzahl bei
    // typischen Produktbildern klein zu halten.
    const MAX_TILE_PIXELS = 1_000_000;

    /**
     * Berechnet pro gridSize x gridSize Zelle den gemittelten R/G/B-Wert
     * UND daraus den Grauwert -- per echtem Box-Averaging aus den
     * tatsächlichen Quellpixeln (siehe Kommentar oben). Liest das Bild in
     * horizontalen 1:1-Streifen (keine Skalierung beim Lesen selbst -> kein
     * Interpolationsverlust), akkumuliert Summe + Anzahl pro Ziel-Bin.
     *
     * Grauwert UND Farbe entstehen im selben Pixel-Durchlauf -- kein
     * zusätzlicher Canvas-Read nötig, da Box-Averaging linear ist
     * (Mittelwert der Helligkeit == Helligkeit des Mittelwerts).
     *
     * @returns {{gray: Float64Array, r: Float64Array, g: Float64Array, b: Float64Array}}
     *          Jeweils Länge gridSize*gridSize, row-major.
     */
    function boxAverageColor(img, gridSize) {
        const sw = img.naturalWidth;
        const sh = img.naturalHeight;

        const sumR = new Float64Array(gridSize * gridSize);
        const sumG = new Float64Array(gridSize * gridSize);
        const sumB = new Float64Array(gridSize * gridSize);
        const counts = new Float64Array(gridSize * gridSize);

        // Ziel-Spalte pro Quell-Spalte vorab berechnen (unabhängig von
        // Tile/Zeile, spart Neuberechnung in der heißen Schleife).
        const destX = new Int32Array(sw);
        for (let x = 0; x < sw; x++) {
            destX[x] = Math.min(gridSize - 1, Math.floor((x * gridSize) / sw));
        }

        const tileHeight = Math.max(1, Math.min(sh, Math.floor(MAX_TILE_PIXELS / sw)));
        const ctx = getSharedCanvasCtx(sw, tileHeight);

        for (let tileY = 0; tileY < sh; tileY += tileHeight) {
            const rows = Math.min(tileHeight, sh - tileY);

            // 1:1-Kopie eines horizontalen Bildstreifens -- bewusst KEINE
            // Skalierung in diesem Schritt, daher keine Aliasing-Quelle.
            ctx.clearRect(0, 0, sw, rows);
            ctx.drawImage(img, 0, tileY, sw, rows, 0, 0, sw, rows);

            const data = ctx.getImageData(0, 0, sw, rows).data;

            for (let y = 0; y < rows; y++) {
                const globalY = tileY + y;
                const destY = Math.min(gridSize - 1, Math.floor((globalY * gridSize) / sh));
                const rowOffset = y * sw;
                const binRowOffset = destY * gridSize;

                for (let x = 0; x < sw; x++) {
                    const p = (rowOffset + x) * 4;
                    const bin = binRowOffset + destX[x];
                    sumR[bin] += data[p];
                    sumG[bin] += data[p + 1];
                    sumB[bin] += data[p + 2];
                    counts[bin] += 1;
                }
            }
        }

        const gray = new Float64Array(gridSize * gridSize);
        for (let i = 0; i < counts.length; i++) {
            const c = counts[i] > 0 ? counts[i] : 1;
            sumR[i] /= c;
            sumG[i] /= c;
            sumB[i] /= c;
            gray[i] = sumR[i] * 0.299 + sumG[i] * 0.587 + sumB[i] * 0.114;
        }

        return { gray, r: sumR, g: sumG, b: sumB };
    }

    /**
     * Mittlere Farbabweichung zwischen zwei Color-Grids, normiert auf 0..1
     * (0 = identisch, 1 = maximale Abweichung in allen Kanälen aller Zellen).
     * Ergänzt den rein strukturellen dHash, der per Definition NICHT
     * zwischen Farbvarianten desselben Musters unterscheidet (siehe
     * Kommentar bei dedupeImagesHybrid).
     */
    function colorGridDistance(a, b) {
        if (!a || !b || a.r.length !== b.r.length) return Infinity;
        const n = a.r.length;
        let sum = 0;
        for (let i = 0; i < n; i++) {
            sum += Math.abs(a.r[i] - b.r[i]) + Math.abs(a.g[i] - b.g[i]) + Math.abs(a.b[i] - b.b[i]);
        }
        return sum / (n * 765); // 765 = 255 * 3 Kanäle
    }

    /**
     * Berechnet Hash + Farbsignatur + Aspect Ratio für ein Bild.
     * @returns {Promise<{hash: string|null, aspectRatio: number|null, colorGrid: Object|null}>}
     */
    async function computeSignature(url, size = 9) {
        const img = await loadImage(url);
        const aspectRatio = img.naturalWidth / img.naturalHeight;

        // size+1, da dHash Differenzen zu Nachbar-Pixeln bildet
        const gridSize = size + 1;

        let grid;
        try {
            grid = boxAverageColor(img, gridSize);
        } catch (e) {
            // Canvas "tainted" durch fehlende CORS-Header -> kein Hash möglich
            return { hash: null, aspectRatio, colorGrid: null };
        }

        const gray = grid.gray;
        let hashH = '';
        let hashV = '';
        for (let y = 0; y < size; y++) {
            for (let x = 0; x < size; x++) {
                const idx = y * gridSize + x;
                hashH += gray[idx] > gray[idx + 1] ? '1' : '0';                // Nachbar rechts
                hashV += gray[idx] > gray[(y + 1) * gridSize + x] ? '1' : '0'; // Nachbar unten
            }
        }

        return {
            hash: hashH + hashV,
            aspectRatio,
            colorGrid: { r: grid.r, g: grid.g, b: grid.b },
        };
    }

    function hammingDistance(a, b) {
        if (!a || !b || a.length !== b.length) return Infinity;
        let dist = 0;
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) dist++;
        }
        return dist;
    }

    // ------------------------------------------------------------
    // 4. Concurrency-Limiter
    // ------------------------------------------------------------
    // Verhindert, dass z.B. 50 Variant-Bilder gleichzeitig geladen
    // und ge-canvas-t werden (Speicher-/Performance-Spitzen).
    async function mapWithConcurrency(items, limit, fn) {
        const results = new Array(items.length);
        let cursor = 0;
        async function worker() {
            while (cursor < items.length) {
                const i = cursor++;
                results[i] = await fn(items[i], i);
            }
        }
        const workers = Array.from({ length: Math.min(limit, items.length) }, worker);
        await Promise.all(workers);
        return results;
    }

    // ------------------------------------------------------------
    // 5. Signature-Cache
    // ------------------------------------------------------------
    // Verhindert Re-Processing, falls dedupeImagesHybrid() mehrfach
    // mit überlappenden URLs aufgerufen wird (z.B. bei Swatch-Wechsel
    // im Custom Element). Cache ist modul-global, nicht pro Aufruf.
    const signatureCache = new Map(); // "url::size" -> Promise<{hash, aspectRatio}>

    function getCachedSignature(url, size) {
        const cacheKey = url + '::' + size;
        if (!signatureCache.has(cacheKey)) {
            signatureCache.set(
                cacheKey,
                computeSignature(url, size).catch(() => ({ hash: null, aspectRatio: null, colorGrid: null }))
            );
        }
        return signatureCache.get(cacheKey);
    }

    function clearSignatureCache() {
        signatureCache.clear();
    }

    // ------------------------------------------------------------
    // 6. Haupt-Dedupe-Funktion
    // ------------------------------------------------------------
    /**
     * @param {string[]} images
     * @param {Object} [options]
     * @param {number} [options.hashSize=9]       Hash-Grid-Größe (9 -> 162 Bit)
     * @param {number} [options.similarityThreshold=0.97]  0..1, 1 = nur exakte Hash-Treffer
     * @param {number} [options.aspectRatioTolerance=0.02] Toleranz für Format-Vorfilter
     * @param {number} [options.colorTolerance=0.08] 0..1, max. erlaubte mittlere
     *        Farbabweichung (siehe colorGridDistance). dHash ist rein strukturell
     *        und erkennt KEINE Farbunterschiede (codiert nur Helligkeits-Gradienten,
     *        nicht deren Betrag) -- zwei Bilder mit identischem Motiv aber
     *        unterschiedlicher Farbvariante (z.B. dasselbe Druckmuster in Beige
     *        und Schwarz) können dieselbe Bit-Struktur erzeugen. Beobachtet bei
     *        einem Handtuch-Motiv: Hamming-Distanz nur 2/162 trotz klar
     *        unterschiedlicher Farbgebung. Daher gilt ein Bild nur als Duplikat,
     *        wenn STRUKTUR (dHash) UND FARBE (colorGridDistance) übereinstimmen.
     * @param {number} [options.concurrency=6]     Parallele Image-Loads pro Gruppe
     * @param {boolean} [options.debug=false]
     * @returns {Promise<string[]>}
     */
    async function dedupeImagesHybrid(images, options = {}) {
        const {
            hashSize = 9,
            similarityThreshold = 0.97,
            aspectRatioTolerance = 0.02,
            colorTolerance = 0.01,
            concurrency = 6,
            debug = false,
        } = options;

        if (!Array.isArray(images) || images.length === 0) return [];

        // --- Stufe 1: Gruppierung nach Product Key ---
        const groups = new Map();
        for (const url of images) {
            const key = extractProductKey(url);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(url);
        }

        const totalBits = hashSize * hashSize * 2; // horizontal + vertikal
        const maxHammingDistance = Math.round(totalBits * (1 - similarityThreshold));

        const result = [];

        for (const [key, urls] of groups.entries()) {
            if (urls.length === 1) {
                result.push(urls[0]);
                continue;
            }

            const sigs = await mapWithConcurrency(urls, concurrency, (url) =>
                getCachedSignature(url, hashSize)
            );

            const anyHashAvailable = sigs.some((s) => s && s.hash !== null);

            if (!anyHashAvailable) {
                // Kein verlässlicher Vergleich möglich (z.B. CORS-Block) ->
                // im Zweifel ALLE behalten statt blind eines zu verwerfen.
                if (debug) {
                    console.warn(
                        `[Dedupe] Keine Hashes für Gruppe "${key}" verfügbar – ${urls.length} Bild(er) werden behalten.`
                    );
                }
                urls.forEach((u) => result.push(u));
                continue;
            }

            const kept = []; // [{ url, sig }]

            for (let i = 0; i < urls.length; i++) {
                const url = urls[i];
                const sig = sigs[i];

                if (!sig || sig.hash === null) {
                    // Einzelnes Bild ohne Hash kann nicht sicher verglichen
                    // werden -> behalten statt zu verwerfen.
                    kept.push({ url, sig: null });
                    continue;
                }

                const isDuplicate = kept.some((k) => {
                    if (!k.sig || k.sig.hash === null) return false;

                    if (k.sig.aspectRatio != null && sig.aspectRatio != null) {
                        const arDiff = Math.abs(sig.aspectRatio - k.sig.aspectRatio);
                        if (arDiff > aspectRatioTolerance) return false; // anderes Format -> kein Duplikat
                    }

                    const structDist = hammingDistance(sig.hash, k.sig.hash);
                    const colorDist = colorGridDistance(sig.colorGrid, k.sig.colorGrid);

                    if (debug) {
                        console.log(
                            `[Dedupe] ${url}\n   vs ${k.url}\n   structDist=${structDist}/${maxHammingDistance}  colorDist=${colorDist.toFixed(4)}/${colorTolerance}`
                        );
                    }

                    // Nur Duplikat, wenn STRUKTUR UND FARBE übereinstimmen.
                    return structDist <= maxHammingDistance && colorDist <= colorTolerance;
                });

                if (!isDuplicate) {
                    kept.push({ url, sig });
                }
            }

            kept.forEach((k) => result.push(k.url));
        }

        return result;
    }

    global.HDTImageDedupe = {
        dedupeImagesHybrid,
        extractProductKey,
        computeSignature,
        hammingDistance,
        colorGridDistance,
        clearSignatureCache,
    };
})(window);